import { useState, useRef, useEffect, FormEvent, KeyboardEvent } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Loader2, User, Bot } from "lucide-react";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface GeminiChatContentProps {
  initialContext: string;
  conversationHistory: ChatMessage[];
  onSendMessage: (message: string) => Promise<void>;
  isLoadingMessage: boolean;
  streamingContent?: string;
}

export function GeminiChatContent({
  initialContext,
  conversationHistory,
  onSendMessage,
  isLoadingMessage,
  streamingContent,
}: GeminiChatContentProps) {
  const [message, setMessage] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Auto-scroll para o fim quando novas mensagens chegam
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [conversationHistory, streamingContent]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (message.trim() && !isLoadingMessage) {
      const msg = message.trim();
      setMessage("");
      setIsExpanded(false);

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      await onSendMessage(msg);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }

    setIsExpanded(e.target.value.length > 100 || e.target.value.includes("\n"));
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Área de mensagens */}
      <ScrollArea ref={scrollAreaRef} className="flex-1 pr-4">
        <div className="space-y-4 pb-4">
          {/* Contexto inicial (primeira mensagem do sistema) */}
          {initialContext && conversationHistory.length === 0 && !isLoadingMessage && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-muted">
                <p className="text-sm text-foreground">
                  Olá! Estou pronto para analisar os dados do exame. Faça sua pergunta.
                </p>
              </div>
            </div>
          )}

          {/* Histórico de mensagens */}
          {conversationHistory.map((msg, index) => (
            <div
              key={index}
              className={cn(
                "flex gap-3",
                msg.role === "user" ? "justify-end" : "justify-start"
              )}
            >
              {msg.role === "assistant" && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
              )}
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-4 py-3",
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                )}
              >
                {msg.role === "assistant" ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm">{msg.content}</p>
                )}
              </div>
              {msg.role === "user" && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                  <User className="h-4 w-4 text-primary-foreground" />
                </div>
              )}
            </div>
          ))}

          {/* Streaming content */}
          {streamingContent && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-muted">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {streamingContent}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}

          {/* Loading indicator */}
          {isLoadingMessage && !streamingContent && (
            <div className="flex gap-3 justify-start">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-muted">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Pensando...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input de mensagem */}
      <div className="pt-4 border-t mt-auto">
        <form onSubmit={handleSubmit} className="group/composer w-full">
          <div
            className={cn(
              "w-full bg-background dark:bg-muted/50 cursor-text overflow-clip bg-clip-padding p-2.5 shadow-md border border-border transition-all duration-200",
              {
                "rounded-2xl grid grid-cols-1 grid-rows-[auto_1fr_auto]": isExpanded,
                "rounded-[20px] grid grid-cols-[1fr_auto] grid-rows-[auto]": !isExpanded,
              }
            )}
            style={{
              gridTemplateAreas: isExpanded
                ? "'primary' 'footer'"
                : "'primary trailing'",
            }}
          >
            <div
              className={cn(
                "flex min-h-12 items-center overflow-x-hidden px-2",
                {
                  "px-2 py-1 mb-0": isExpanded,
                }
              )}
              style={{ gridArea: "primary" }}
            >
              <div className="flex-1 overflow-auto max-h-32">
                <Textarea
                  ref={textareaRef}
                  value={message}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Faça uma pergunta sobre o exame..."
                  disabled={isLoadingMessage}
                  className="min-h-0 resize-none rounded-none border-0 p-0 text-sm placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 scrollbar-thin dark:bg-transparent disabled:opacity-50"
                  rows={1}
                />
              </div>
            </div>

            <div
              className="flex items-center gap-2"
              style={{ gridArea: isExpanded ? "footer" : "trailing" }}
            >
              <div className="ml-auto flex items-center gap-1.5">
                {isLoadingMessage ? (
                  <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : message.trim() ? (
                  <Button
                    type="submit"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    disabled={isLoadingMessage}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
